import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTag, FaCalendarAlt, FaBullseye } from "react-icons/fa";
import PromUserContextMenu from "./PromUserContextMenu";
import PromotionalPublicProfileModal from "./PromotionalPublicProfileModal";
import ReportUserModal from "./ReportUserModal";
import FirstMessageToPromotionalModal from "./FirstMessageToPromotionalModal";

function formatDate(dateStr) {
  const options = { day: "numeric", month: "long" };
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-ES", options);
}

function isVideo(url) {
  // Detecta si el archivo es video por su extensión
  return /\.(mp4|webm|ogg|mov)$/i.test(url);
}

function PromotionalTweet({ promo }) {
  const {
    title,
    description,
    descuento,
    fechaInicio,
    fechaFin,
    images,
    ctaLink,
    ctaTexto,
    categoria,
    objetivo,
    promoProfile,
  } = promo;

  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const [contextMenuPos, setContextMenuPos] = useState({ top: 0, left: 0 });
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [open, setOpen] = useState(false); // Modal para enviar primer mensaje


  // Estado para el modal de reporte
  const [isReportOpen, setIsReportOpen] = useState(false);

  // GALERÍA: Estado para la galería de imágenes
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  const openUserContextMenu = (e, user) => {
    e.preventDefault();
    e.stopPropagation();

    const triggerRect = e.currentTarget.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect() || {
      top: 0,
      left: 0,
    };

    let top = triggerRect.top - containerRect.top + triggerRect.height / 2;
    let left = triggerRect.left - containerRect.left + triggerRect.width + 8;

    if (left > window.innerWidth - 200) left -= 180;

    setContextMenuPos({ top, left });
    setSelectedUser(user);
    setContextMenuOpen(true);
  };

  const closeContextMenu = () => {
    setContextMenuOpen(false);
    setSelectedUser(null);
  };

  const handleVerPerfil = () => {
    setShowProfileModal(true);
    setContextMenuOpen(false);
  };

  const handleCloseProfileModal = () => {
    setShowProfileModal(false);
  };

  // Funciones para abrir y cerrar el modal de reporte
  const handleOpenReport = () => {
    setIsReportOpen(true);
    setContextMenuOpen(false);
  };

  const handleCloseReport = () => {
    setIsReportOpen(false);
    closeContextMenu();
  };

  const handleImageClick = (index) => {
    setActiveImageIndex(index);
    setIsGalleryOpen(true);
  };

  const handleCloseGallery = () => {
    setIsGalleryOpen(false);
  };

const handleNextImage = () => {
  setActiveImageIndex((prev) => (prev + 1) % media.length);
};

const handlePrevImage = () => {
  setActiveImageIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
};

  const media = [
  ...(promo.images?.map(url => ({ url, type: 'image' })) || []),
  ...(promo.videos?.map(url => ({ url, type: 'video' })) || []),
];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        !containerRef.current.contains(e.target)
      ) {
        closeContextMenu();
      }
    };

    const handleEsc = (e) => {
      if (e.key === "Escape") {
        closeContextMenu();
        setIsGalleryOpen(false); // cerrar galería también
      }
    };

    if (contextMenuOpen || isGalleryOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEsc);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [contextMenuOpen, isGalleryOpen]);

  return (
    <div ref={containerRef} className="relative">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{
          scale: 1.05,
          boxShadow: "0 15px 30px rgba(255, 197, 0, 0.4)",
        }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="bg-gradient-to-tr from-yellow-400 via-yellow-300 to-yellow-200 dark:from-yellow-800 dark:via-yellow-700 dark:to-yellow-900 border border-yellow-500 dark:border-yellow-600 rounded-3xl shadow-lg max-w-xl mx-auto p-6 cursor-pointer"
      >
        <div className="absolute top-4 right-4">
          <button
            onClick={() => setOpen(true)}
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-full shadow transition"
            title="Enviar mensaje"
          >
            📩
          </button>
        </div>

        {promoProfile && (
          <div className="flex items-center gap-3 mb-4">
            <img
              src={promoProfile.profilePhotoUrl}
              alt={promoProfile.name}
              className="w-10 h-10 rounded-full object-cover border border-yellow-500 shadow"
            />
            <span
              onClick={(e) => openUserContextMenu(e, promoProfile)}
              className="font-semibold text-yellow-900 dark:text-yellow-100 text-lg cursor-pointer hover:underline"
            >
              {promoProfile.name}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-3xl font-extrabold text-yellow-900 dark:text-yellow-100 tracking-wide drop-shadow-md uppercase">
            {title}
          </h2>
          <span className="text-yellow-800 dark:text-yellow-300 font-semibold text-lg">
            {descuento}
          </span>
        </div>

        {media[0] && (
          media[0].type === "video" ? (
            <motion.video
              src={media[0].url}
              className="w-full h-52 object-cover rounded-xl mb-5 shadow-md cursor-pointer"
              controls
              onClick={() => handleImageClick(0)}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            />
          ) : (
            <motion.img
              src={media[0].url}
              alt={title}
              className="w-full h-52 object-cover rounded-xl mb-5 shadow-md cursor-pointer"
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.5 }}
              onClick={() => handleImageClick(0)}
            />
          )
        )}

        <p className="text-yellow-900 dark:text-yellow-100 mb-4 text-lg leading-relaxed">
          {description}
        </p>

        <div className="flex flex-wrap gap-4 text-yellow-900 dark:text-yellow-300 mb-5">
          
          {(fechaInicio && fechaFin) && (
            <div className="flex items-center space-x-2">
              <FaCalendarAlt className="text-yellow-700 dark:text-yellow-400" />
              <span className="text-sm">
                Del <strong>{formatDate(fechaInicio)}</strong> al{" "}
                <strong>{formatDate(fechaFin)}</strong>
              </span>
            </div>
          )}

          
        </div>

        {ctaTexto ? (
          ctaLink ? (
            <a
              href={ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-full shadow-lg transition"
            >
              {ctaTexto}
            </a>
          ) : (
            <button
              disabled
              className="inline-block px-6 py-3 bg-yellow-400 text-yellow-900 font-semibold rounded-full cursor-not-allowed opacity-70"
            >
              {ctaTexto}
            </button>
          )
        ) : null}
      </motion.div>

      {contextMenuOpen && selectedUser && (
        <div ref={menuRef} className="absolute z-50" style={contextMenuPos}>
          <PromUserContextMenu
            user={selectedUser}
            onClose={closeContextMenu}
            onVerPerfil={handleVerPerfil}
            onReport={handleOpenReport} // Pasamos la función para abrir el modal
          />
        </div>
      )}

      {showProfileModal && selectedUser && (
        <PromotionalPublicProfileModal
          user={selectedUser}
          promoProfileId={selectedUser.uid}
          onClose={handleCloseProfileModal}
        />
      )}

      {/* Modal para reportar usuario */}
      <ReportUserModal
        isOpen={isReportOpen}
        onClose={handleCloseReport}
        user={selectedUser}
      />

      {promoProfile && (
        <FirstMessageToPromotionalModal
          open={open}
          onClose={() => setOpen(false)}
          promoProfileId={promoProfile.uid}
          promoProfileName={promoProfile.name}
        />
      )}


      {/* GALERÍA MODAL */}
      <AnimatePresence>
        {isGalleryOpen && media?.length > 0 && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseGallery}
          >
            {media[activeImageIndex].type === "video" ? (
              <motion.video
                src={media[activeImageIndex].url}
                className="max-w-[90%] max-h-[80%] rounded-2xl shadow-xl"
                controls
                autoPlay
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <motion.img
                src={media[activeImageIndex].url}
                alt="Gallery"
                className="max-w-[90%] max-h-[80%] rounded-2xl shadow-xl"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                onClick={(e) => e.stopPropagation()}
              />
            )}

            {media.length > 1 && (
              <div className="absolute bottom-10 flex gap-5">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrevImage();
                  }}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-full"
                >
                  ⬅
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextImage();
                  }}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-full"
                >
                  ➡
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default PromotionalTweet;
